import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';

interface RequireContractorProps {
  children: React.ReactNode;
}

/**
 * Bounces explicit home/manage sessions off contractor-only screens.
 *
 * The Shell already hides contractor nav outside contractor mode, but a URL
 * can survive a mode switch (refresh, browser Back, impersonation landing on
 * the previous page). Without this guard a home-mode user stares at the
 * contractor portal with resident chrome around it. `/app` resolves to the
 * correct dashboard for whatever the active mode is.
 *
 * Only a trusted mode redirects: an appMode the user actually holds (or any
 * appMode while the modes list is still loading is never trusted against).
 * Superadmins, pure contractors, and loading states render normally.
 */
export const RequireContractor: React.FC<RequireContractorProps> = ({ children }) => {
  const { user, appMode } = useAuth();

  const modes = user?.available_modes;
  const trustedMode =
    appMode && (!Array.isArray(modes) || modes.includes(appMode)) ? appMode : null;

  if (trustedMode === 'home' || trustedMode === 'manage') {
    return <Navigate to="/app" replace />;
  }

  return <>{children}</>;
};
